import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useEffect, useState } from "react";
import { CustomBox } from "../../common/BoxAbstraction";
import EmptyView from "../../common/EmptyView";
import CollapsableHeader from "../../common/CollapsableHeader";
import { content } from "../utils";

// ── constants ────────────────────────────────────────────────────────────────
const PALETTE      = ["#2563eb","#7c3aed","#0d9488","#f59e0b","#ec4899","#06b6d4","#94a3b8"];
const PALETTE_BG   = ["#eff6ff","#f5f3ff","#f0fdfa","#fffbeb","#fdf2f8","#ecfeff","#f1f5f9"];
const PALETTE_TEXT = ["#2563eb","#7c3aed","#0d9488","#d97706","#db2777","#0891b2","#475569"];
const CIRCUMFERENCE = 2 * Math.PI * 15.9; // ≈ 99.9

// ── helper: build sorted segments from raw package_wise data ─────────────────
const computeSegments = (packageWise) => {
  if (!packageWise?.keys?.length) return [];

  const pairs = packageWise.keys
    .map((name, i) => ({ name, count: parseInt(packageWise.values[i], 10) || 0 }))
    .filter((p) => p.name !== "")
    .sort((a, b) => b.count - a.count);

  const total = pairs.reduce((s, p) => s + p.count, 0);
  if (!total) return [];

  const top = pairs.slice(0, 6);
  const othersCount = pairs.slice(6).reduce((s, p) => s + p.count, 0);

  const rows = top.map((p, i) => ({
    name: p.name,
    count: p.count,
    pct: Math.round((p.count / total) * 100),
    color: PALETTE[i],
    bg: PALETTE_BG[i],
    textColor: PALETTE_TEXT[i],
  }));

  if (othersCount > 0) {
    rows.push({
      name: "Others",
      count: othersCount,
      pct: Math.round((othersCount / total) * 100),
      color: PALETTE[6],
      bg: PALETTE_BG[6],
      textColor: PALETTE_TEXT[6],
    });
  }

  // attach donut dasharray/dashoffset
  let offset = 0;
  return rows.map((row) => {
    const len = (row.pct / 100) * CIRCUMFERENCE;
    const seg = {
      ...row,
      dasharray: `${len.toFixed(2)} ${(CIRCUMFERENCE - len).toFixed(2)}`,
      dashoffset: -offset,
    };
    offset += len;
    return seg;
  });
};

// ── component ─────────────────────────────────────────────────────────────────
const PackageWiseAnalysis = () => {
  const [homepageData, setHomepageData] = useState(null);
  const [timePeriod, setTimePeriod] = useState("currentMonth");

  const loadData = () => {
    const raw = localStorage.getItem("homepageData");
    if (raw) {
      try { setHomepageData(JSON.parse(raw)); } catch { /* ignore */ }
    }
  };

  useEffect(() => {
    loadData();
    window.addEventListener("storage", loadData);
    return () => window.removeEventListener("storage", loadData);
  }, []);

  const packageWise =
    homepageData?.data?.[
      timePeriod === "currentMonth" ? "current_month" : "previous_month"
    ]?.package_wise;

  const segments = computeSegments(packageWise);
  const maxCount = segments.length > 0 ? segments[0].count : 1;
  const namedCount = segments.filter((s) => s.name !== "Others").length;

  return (
    <Box flexGrow={1}>
      <Accordion component="Box">
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="pkg-breakdown-content"
          id="pkg-breakdown-header"
        >
          <CollapsableHeader
            title="Subscription Usage — Health Package Breakdown"
            subheading="Distribution of health packages used in the selected period."
            tooltipText="Shows the top packages by report count for the chosen time period, along with each package's share."
          />
        </AccordionSummary>

        <AccordionDetails>
          <CustomBox>
            {/* ── Card header ── */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              flexWrap="wrap"
              gap={1}
              mb={3}
            >
              <Box>
                <Typography
                  sx={{ fontWeight: 700, fontSize: "18px", color: "#1e293b" }}
                >
                  Subscription Usage — Health Package Breakdown
                </Typography>
              </Box>

              <Stack direction="row" alignItems="center" gap={1}>
                <Typography sx={{ fontSize: "13px", color: "#94a3b8" }}>
                  Time Period
                </Typography>
                <Select
                  size="small"
                  value={timePeriod}
                  onChange={(e) => setTimePeriod(e.target.value)}
                  sx={{ fontSize: "13px", minWidth: 160, borderRadius: "8px" }}
                >
                  <MenuItem value="currentMonth">Current Month</MenuItem>
                  <MenuItem value="previousMonth">Previous Month</MenuItem>
                </Select>
              </Stack>
            </Stack>

            {/* ── Body ── */}
            {!homepageData ? (
              /* skeleton layout while localStorage hasn't loaded yet */
              <Stack direction="row" gap={4} flexWrap={{ xs: "wrap", md: "nowrap" }} alignItems="flex-start">
                <Stack alignItems="center" gap={2} sx={{ minWidth: 200 }}>
                  <Skeleton variant="circular" width={180} height={180} />
                  {[1,2,3,4].map((k) => (
                    <Stack key={k} direction="row" alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
                      <Stack direction="row" gap={1} alignItems="center">
                        <Skeleton variant="circular" width={10} height={10} />
                        <Skeleton variant="text" width={110} height={14} />
                      </Stack>
                      <Skeleton variant="text" width={28} height={14} />
                    </Stack>
                  ))}
                </Stack>
                <Stack flexGrow={1} gap={1.5}>
                  <Stack direction="row" gap={2}>
                    {[30, 200, 120, 60].map((w, i) => <Skeleton key={i} variant="text" width={w} height={14} />)}
                  </Stack>
                  {[1,2,3,4,5].map((k) => (
                    <Stack key={k} direction="row" gap={2} alignItems="center">
                      <Skeleton variant="text" width={20} height={14} />
                      <Skeleton variant="text" width={220} height={14} />
                      <Skeleton variant="rectangular" width={120} height={6} sx={{ borderRadius: 3 }} />
                      <Skeleton variant="rectangular" width={72} height={22} sx={{ borderRadius: "6px" }} />
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            ) : segments.length === 0 ? (
              <Stack alignItems="center" justifyContent="center" gap={2} py={5}>
                <Box sx={{ width: 72, height: 72, borderRadius: "50%", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                    <path d="M20 7H4C2.9 7 2 7.9 2 9v10c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2z" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 3H8L6 7h12l-2-4z" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 12v4M10 14h4" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#475569", mb: 0.5 }}>No Package Data Available</Typography>
                  <Typography sx={{ fontSize: "12px", color: "#94a3b8", maxWidth: 300 }}>Package distribution data hasn't been recorded for this period. Try switching to a different time range.</Typography>
                </Box>
              </Stack>
            ) : (
              <Stack
                direction="row"
                gap={4}
                flexWrap={{ xs: "wrap", md: "nowrap" }}
                alignItems="flex-start"
              >
                {/* ── Left: Donut + Legend ── */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                    minWidth: 200,
                  }}
                >
                  {/* SVG Donut */}
                  <Box sx={{ position: "relative", width: 180, height: 180 }}>
                    <svg
                      viewBox="0 0 36 36"
                      style={{
                        width: "100%",
                        height: "100%",
                        transform: "rotate(-90deg)",
                      }}
                    >
                      {segments.map((seg, i) => (
                        <circle
                          key={i}
                          cx="18"
                          cy="18"
                          r="15.9"
                          fill="none"
                          stroke={seg.color}
                          strokeWidth="3.8"
                          strokeDasharray={seg.dasharray}
                          strokeDashoffset={seg.dashoffset}
                        />
                      ))}
                    </svg>

                    {/* Center label */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        textAlign: "center",
                        pointerEvents: "none",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "28px",
                          lineHeight: 1,
                          color: "#1e293b",
                        }}
                      >
                        {namedCount}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "11px", color: "#94a3b8", mt: "2px" }}
                      >
                        Packages
                      </Typography>
                    </Box>
                  </Box>

                  {/* Legend */}
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.75,
                    }}
                  >
                    {segments.map((seg, i) => (
                      <Stack
                        key={i}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                      >
                        <Stack direction="row" alignItems="center" gap={0.75}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              background: seg.color,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#374151",
                              maxWidth: 140,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {seg.name}
                          </Typography>
                        </Stack>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#374151",
                            flexShrink: 0,
                          }}
                        >
                          {seg.pct}%
                        </Typography>
                      </Stack>
                    ))}
                  </Box>
                </Box>

                {/* ── Right: Table ── */}
                <TableContainer sx={{ flexGrow: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {["#", "Health Package", "Share", "Count"].map((h) => (
                          <TableCell
                            key={h}
                            sx={{
                              fontSize: "11px",
                              fontWeight: 600,
                              color: "#94a3b8",
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                              borderBottom: "1px solid #e2e8f0",
                              pb: 1.5,
                              whiteSpace: "nowrap",
                              ...(h === "Share" ? { minWidth: 140 } : {}),
                            }}
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {segments.map((seg, i) => (
                        <TableRow
                          key={i}
                          sx={{
                            "&:last-child td": { border: 0 },
                            "&:hover": { background: "#f8fafc" },
                          }}
                        >
                          {/* # */}
                          <TableCell
                            sx={{
                              color: "#94a3b8",
                              fontSize: "12px",
                              width: 28,
                              py: 1.5,
                            }}
                          >
                            {seg.name === "Others" ? "" : i + 1}
                          </TableCell>

                          {/* Health Package */}
                          <TableCell
                            sx={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: "#1e293b",
                              py: 1.5,
                            }}
                          >
                            {seg.name}
                          </TableCell>

                          {/* Share — mini bar */}
                          <TableCell sx={{ minWidth: 140, py: 1.5 }}>
                            <Box
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                background: "#e2e8f0",
                                overflow: "hidden",
                              }}
                            >
                              <Box
                                sx={{
                                  height: "100%",
                                  width: `${Math.round(
                                    (seg.count / maxCount) * 100
                                  )}%`,
                                  background: seg.color,
                                  borderRadius: 3,
                                }}
                              />
                            </Box>
                          </TableCell>

                          {/* Count chip */}
                          <TableCell sx={{ py: 1.5 }}>
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                background: seg.bg,
                                color: seg.textColor,
                                fontWeight: 600,
                                fontSize: "12px",
                                borderRadius: "6px",
                                px: 1,
                                py: 0.4,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {seg.count.toLocaleString("en-IN")}
                              <Box
                                component="span"
                                sx={{ opacity: 0.65, fontWeight: 400 }}
                              >
                                &nbsp;{seg.pct}%
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            )}
          </CustomBox>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default PackageWiseAnalysis;
